import { TreeNode, ViewerEdge, ViewerNode } from '../models/graph-viewer.models';

export interface SearchTrunkGroup<T = ViewerEdge> {
  key: string;
  sourceId: string;
  label: string;
  kind: 'search' | 'collection' | 'related';
  links: T[];
  lane: number;
  laneCount: number;
  /** Reserved connector corridor, relative to the parent's compact X position. */
  railOffset?: number;
}

export function graphEndpointId(endpoint: any): string {
  return String(endpoint?.id ?? endpoint ?? '');
}

/** Group only siblings; shared collections have no single originating search. */
export function groupSearchTrunks<T extends ViewerEdge>(
  links: T[], nodes: ViewerNode[], _rootId: string,
): SearchTrunkGroup<T>[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const groups = new Map<string, SearchTrunkGroup<T>>();
  const byParent = new Map<string, SearchTrunkGroup<T>[]>();
  links.forEach((link) => {
    // Manual entries connect directly to the root, without a search junction.
    if (link.type === 'manual_relationship' || link.raw?.type === 'manual_relationship') return;
    const sourceId = graphEndpointId(link.source);
    const child = byId.get(graphEndpointId(link.target))?.raw;
    const meta = child?.metadata ?? {};
    const edgeMeta = link.raw?.metadata ?? {};
    // Returned photos expose image-search actions, but their image-valued
    // metadata is not evidence that an image/face search was performed.
    const collectedPhoto = child?.type === 'face_image' || (link.type || link.raw?.type) === 'has_face_image';
    const collection = collectedPhoto || meta.isAggregateCollection || meta.isIdentityHub || edgeMeta.aggregateCollection ||
      ['layer_summary', 'has_names', 'has_photos'].includes(String(link.type));
    const queryType = collection ? '' : String(edgeMeta.searchedValueType || meta.searchedValueType || '').toLowerCase();
    const actionId = collection ? '' : String(edgeMeta.originActionId || meta.originActionId || '');
    const value = collection ? '' : String(edgeMeta.searchedValue || meta.searchedValue || '');
    const actionType = String(edgeMeta.actionType || edgeMeta.action_type || meta.actionType || meta.action_type ||
      (child?.type === 'leak_source' || meta.sourceContext === 'ladder_leak_source' ? 'leak_search' :
        ['image_query', 'face_result'].includes(child?.type) ? 'face_search' : 'osint_search')).toLowerCase();
    const typeLabel: Record<string, string> = { email: 'Email', phone: 'Phone', username: 'Username', name: 'Name', image: 'Image' };
    const kind = collection ? 'collection' : actionId || queryType ? 'search' : 'related';
    const label = collectedPhoto ? 'Collected Photos' : collection ? 'Collected Values' : queryType ? (actionType === 'face_search' ? 'Face Search' :
      `${actionType === 'leak_search' ? 'Leak' : 'OSINT'} ${typeLabel[queryType] || queryType} Search`) :
      actionId ? 'Search Results' : 'Related Results';
    const provenance = collectedPhoto ? 'collected_photos' : actionId || (queryType ? JSON.stringify([actionType, queryType, value]) : kind);
    const key = JSON.stringify([sourceId, kind === 'search' ? actionType : kind, provenance]);
    let group = groups.get(key);
    if (!group) {
      group = { key, sourceId, label, kind, links: [], lane: 0, laneCount: 1 };
      groups.set(key, group);
      const siblings = byParent.get(sourceId) ?? [];
      siblings.push(group);
      byParent.set(sourceId, siblings);
    }
    group.links.push(link);
  });
  const order = { search: 0, related: 1, collection: 2 };
  byParent.forEach((siblings) => siblings.sort((a, b) => order[a.kind] - order[b.kind]).forEach((group, lane) => {
    group.lane = lane;
    group.laneCount = siblings.length;
  }));
  return [...byParent.values()].flat();
}

export interface TrunkPoint { x: number; y: number; }

/** Wider fans need a longer shared stem before the searches separate. */
export function searchFanStemLength(horizontalSpan: number): number {
  return 80 + Math.min(720, Math.max(0, horizontalSpan) * 0.18);
}

/** A drawing-only junction shared by all results of one search. */
export function searchFanGeometry(source: TrunkPoint, targets: TrunkPoint[], placedJunction?: TrunkPoint, sharedStemLength?: number): {
  stem: TrunkPoint[]; junction: TrunkPoint; label: TrunkPoint;
} {
  const xs = targets.length ? targets.map((target) => target.x) : [source.x];
  const firstY = targets.length ? Math.min(...targets.map((target) => target.y)) : source.y + 320;
  const span = Math.max(source.x, ...xs) - Math.min(source.x, ...xs);
  const stemLength = sharedStemLength ?? Math.min(searchFanStemLength(span), Math.max(0, firstY - source.y - 120));
  const junction = placedJunction ?? {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: source.y + stemLength + 60,
  };
  return {
    stem: [source, { x: source.x, y: placedJunction && sharedStemLength == null
      ? source.y + Math.min(stemLength, Math.max(0, placedJunction.y - source.y - 30))
      : source.y + stemLength }, junction],
    junction,
    label: { x: junction.x, y: junction.y - 20 },
  };
}

/** Search-specific result subtrees, never the source or its other search groups. */
export function searchGroupDragMembers(tree: TreeNode | null, group: SearchTrunkGroup): Set<string> {
  const roots = new Set(group.links.map(link => graphEndpointId(link.target)));
  const members = new Set<string>();
  const visit = (branch: TreeNode, included: boolean): void => {
    if (branch.node.id === group.sourceId) included = false;
    else included = included || roots.has(branch.node.id);
    if (included) members.add(branch.node.id);
    branch.children.forEach(child => visit(child, included));
  };
  if (tree) visit(tree, false);
  roots.forEach(id => { if (id !== group.sourceId) members.add(id); });
  return members;
}

/** Live coordinates keep routes attached during dragging and zoom spacing. */
export function searchTrunkGeometry(
  source: TrunkPoint, targets: TrunkPoint[], railX?: number,
): { stem: TrunkPoint[]; label: TrunkPoint; bus: TrunkPoint[]; buses: TrunkPoint[][]; rail: TrunkPoint[]; branchStarts: TrunkPoint[] } {
  const xs = targets.length ? targets.map((target) => target.x) : [source.x];
  const rows = [...new Set(targets.map((target) => target.y))].sort((a, b) => a - b);
  if (!rows.length) rows.push(source.y + 208);
  const firstRow = targets.filter((target) => target.y === rows[0]);
  const firstXs = firstRow.length ? firstRow.map((target) => target.x) : xs;
  const x = (Math.min(...firstXs) + Math.max(...firstXs)) / 2;
  const corridorX = railX ?? Math.min(...xs) - 116;
  const buses = rows.map((y) => {
    const rowXs = targets.filter((target) => target.y === y).map((target) => target.x);
    if (!rowXs.length) rowXs.push(source.x);
    return [
      { x: rows.length > 1 ? corridorX : Math.min(...rowXs), y: y - 90 },
      { x: Math.max(...rowXs), y: y - 90 },
    ];
  });
  const busY = buses[0][0].y;
  return {
    stem: [source, { x, y: busY - 46 }, { x, y: busY }],
    label: { x, y: busY - 24 },
    bus: buses[0], buses,
    rail: rows.length > 1 ? [{ x: corridorX, y: busY }, { x: corridorX, y: buses[buses.length - 1][0].y }] : [],
    branchStarts: targets.map((target) => ({ x: target.x, y: target.y - 90 })),
  };
}

/** Trace only the shared segments needed to reach the active children. */
export function searchTrunkActivePaths(
  route: ReturnType<typeof searchTrunkGeometry>, targetIndexes: number[],
): TrunkPoint[][] {
  const starts = targetIndexes.map((index) => route.branchStarts[index]).filter(Boolean);
  if (!starts.length) return [];
  const paths = [route.stem];
  const junction = route.stem[route.stem.length - 1];
  starts.forEach((start) => {
    if (route.rail.length && start.y !== junction.y) {
      const railStart = route.rail[0];
      paths.push([junction, railStart]);
      paths.push([railStart, { x: railStart.x, y: start.y }, start]);
    } else {
      paths.push([junction, start]);
    }
  });
  return paths;
}

/**
 * Reserve disjoint subtree columns and global depth bands. Every row at one
 * depth finishes before the next depth begins, across all searches and parents.
 */
export function computeSearchTrunkLayout(
  tree: TreeNode | null, groups: SearchTrunkGroup[],
): Record<string, TrunkPoint> {
  if (!tree) return {};
  const nodeWidth = 168;
  const siblingGap = 40;
  const groupGap = 80;
  const rowGap = 220;
  const levelGap = 320;
  const rowSpacing = (depth: number): number => depth === 1 ? 160 : rowGap;
  const groupsByParent = new Map<string, SearchTrunkGroup[]>();
  groups.forEach((group) => {
    const siblings = groupsByParent.get(group.sourceId) ?? [];
    siblings.push(group);
    groupsByParent.set(group.sourceId, siblings);
  });
  interface Row { children: TreeNode[]; width: number; }
  interface Block { rows: Row[]; expanded: Row; width: number; corridor: number; group?: SearchTrunkGroup; }
  const blocksByNode = new Map<string, Block[]>();
  const widths = new Map<string, number>();
  const rowsByDepth = new Map<number, number>([[0, 1]]);
  const clearanceByDepth = new Map<number, number>();
  const measure = (parent: TreeNode, depth: number): number => {
    parent.children.forEach((child) => measure(child, depth + 1));
    const childrenById = new Map(parent.children.map((child) => [child.node.id, child]));
    const assigned = new Set<string>();
    const blocks: Block[] = [];
    const addBlock = (children: TreeNode[], group?: SearchTrunkGroup): void => {
      if (!children.length) return;
      children.forEach((child) => assigned.add(child.node.id));
      const rows: Row[] = [];
      const rowWidth = (members: TreeNode[]): number => members.reduce((sum, child) => sum + widths.get(child.node.id)!, 0) +
        siblingGap * Math.max(0, members.length - 1);
      const addRow = (members: TreeNode[]): void => {
        if (!members.length) return;
        rows.push({ children: members, width: rowWidth(members) });
      };
      const leaves = children.filter((child) => !child.children.length);
      for (let i = 0; i < leaves.length; i += 4) addRow(leaves.slice(i, i + 4));
      // Expanded siblings need separate columns so their descendants cannot
      // overlap in the next global depth band.
      const branches = children.filter((child) => child.children.length);
      const expanded = { children: branches, width: rowWidth(branches) };
      const rowCount = rows.length + (branches.length ? 1 : 0);
      rowsByDepth.set(depth + 1, Math.max(rowsByDepth.get(depth + 1) || 1, rowCount));
      const corridor = depth > 0 && rowCount > 1 ? 48 : 0;
      blocks.push({ rows, expanded, corridor, group,
        width: Math.max(...rows.map((row) => row.width + corridor +
          (depth === 0 && branches.length && row.children.length % 2 ? nodeWidth + siblingGap : 0)), expanded.width + corridor,
          (group?.label.length ?? 0) * 11 + 24),
      });
    };
    (groupsByParent.get(parent.node.id) ?? []).forEach((group) => {
      const children = group.links.map((link) => childrenById.get(graphEndpointId(link.target)))
        .filter((child): child is TreeNode => !!child && !assigned.has(child.node.id));
      addBlock(children, group);
    });
    // Pack metadata-free root children together as well, rather than giving
    // each leaf a separate column outside the large subtrees.
    const remaining = parent.children.filter((child) => !assigned.has(child.node.id));
    if (depth === 0) addBlock(remaining);
    else remaining.forEach((child) => addBlock([child]));
    // Collections consistently occupy the right side, including with legacy
    // children that do not carry search provenance.
    blocks.sort((a, b) => Number(a.group?.kind === 'collection') - Number(b.group?.kind === 'collection'));
    blocksByNode.set(parent.node.id, blocks);
    const width = Math.max(nodeWidth, blocks.reduce((sum, block) => sum + block.width, 0) +
      groupGap * Math.max(0, blocks.length - 1));
    widths.set(parent.node.id, width);
    if (parent.children.length) {
      clearanceByDepth.set(depth + 1, Math.max(clearanceByDepth.get(depth + 1) ?? 0,
        searchFanStemLength(Math.max(0, width - nodeWidth)) + 240));
    }
    return width;
  };
  const width = measure(tree, 0);
  const depthStarts = new Map<number, number>([[0, 0]]);
  const maxDepth = Math.max(...rowsByDepth.keys());
  for (let depth = 1; depth <= maxDepth; depth++) {
    depthStarts.set(depth, depthStarts.get(depth - 1)! +
      rowSpacing(depth - 1) * ((rowsByDepth.get(depth - 1) || 1) - 1) +
      Math.max(depth === 1 ? 420 : levelGap, clearanceByDepth.get(depth) ?? 0));
  }
  const positions: Record<string, TrunkPoint> = {};
  const place = (parent: TreeNode, left: number, depth: number, row: number): void => {
    const parentWidth = widths.get(parent.node.id)!;
    positions[parent.node.id] = { x: left + parentWidth / 2 - width / 2, y: depthStarts.get(depth)! + row * rowSpacing(depth) };
    const blocks = blocksByNode.get(parent.node.id)!;
    const contentWidth = blocks.reduce((sum, block) => sum + block.width, 0) +
      groupGap * Math.max(0, blocks.length - 1);
    let blockLeft = left + (parentWidth - contentWidth) / 2;
    blocks.forEach((block) => {
      const corridor = block.corridor;
      if (block.group) block.group.railOffset = blockLeft + 16 - width / 2 - positions[parent.node.id].x;
      const placeRow = (members: Row, rowIndex: number): void => {
        let childLeft = blockLeft + corridor + (block.width - corridor - members.width) / 2;
        // Leave the central root-to-branch path clear in short, odd-sized rows.
        if (depth === 0 && block.expanded.children.length && members !== block.expanded && members.children.length % 2) {
          childLeft += (nodeWidth + siblingGap) / 2;
        }
        members.children.forEach((child) => {
          place(child, childLeft, depth + 1, rowIndex);
          childLeft += widths.get(child.node.id)! + siblingGap;
        });
      };
      block.rows.forEach(placeRow);
      placeRow(block.expanded, (rowsByDepth.get(depth + 1) || 1) - 1);
      blockLeft += block.width + groupGap;
    });
  };
  place(tree, 0, 0, 0);
  const rootGroups = groupsByParent.get(tree.node.id) ?? [];
  const centers = (collection: boolean): number[] => rootGroups
    .filter(group => (group.kind === 'collection') === collection)
    .map(group => group.links.map(link => positions[graphEndpointId(link.target)]).filter(Boolean))
    .filter(points => points.length)
    .map(points => (Math.min(...points.map(point => point.x)) + Math.max(...points.map(point => point.x))) / 2);
  const searches = centers(false);
  const collections = centers(true);
  if (searches.length && collections.length) {
    // Center the root between its two categories rather than across the total
    // descendant width (a large search subtree otherwise pulls it to one side).
    const searchCenter = (Math.min(...searches) + Math.max(...searches)) / 2;
    const collectionCenter = (Math.min(...collections) + Math.max(...collections)) / 2;
    positions[tree.node.id].x = (searchCenter + collectionCenter) / 2;
    const offset = positions[tree.node.id].x;
    Object.values(positions).forEach(point => { point.x -= offset; });
    rootGroups.forEach(group => { if (group.railOffset != null) group.railOffset -= offset; });
  }
  return positions;
}
